import React, { useEffect, useState } from "react";
import { Descriptions, Tag, Spin, Empty, Row, Col } from "antd";
import {
  getRiskEvaluation,
  getCraResult,
  getOrderAttachments,
  FileType,
  getOrderDetail,
  getProductCompanyList,
  getCraAttachmentConfig,
  useMetadataStore,
  type OrderCra,
  type OrderAttachmentDTO,
  type ProductCompanyListVO,
  type HighPremiumOrder,
  type OrderAttachmentConfig,
} from "@ifa/utils";
import { FilePreviewModal } from "../../components/FilePreviewModal";
import { HighPremiumOrderCard } from "./components/HighPremiumOrderCard";
import pdfIcon from "../../assets/img/pdf.png";
import excelIcon from "../../assets/img/excel.png";
import wordIcon from "../../assets/img/word.png";
import {
  formatZuuRiskValue,
  parseZuuRiskAnswers,
  parseZuuRiskProjectResults,
  resolveZuuRiskAnswer,
  type ZuuRiskOptionLabels,
  type ZuuRiskValueKind,
} from "../customers/CustomerDetail/components/zuu/zuuRiskMapping.js";
import { resolveCraPresentationVariant } from "./craVariant";
import styles from "./RiskAssessmentView.module.css";

interface RiskAssessmentViewProps {
  orderId?: string;
}

// 侧边栏菜单项配置 - 基础菜单项
const BASE_SIDEBAR_SECTIONS = [
  { key: "customerRisk", label: "客戶風險", project: "CUSTOMER_RISK" },
  { key: "payment", label: "保費支付方式" },
  { key: "distribution", label: "分銷/交付風險", project: "DISTRIBUTION_OR_DELIVERY_RISK" },
  { key: "location", label: "國家或地區風險", project: "COUNTRY_OR_REGIONAL_RISK" },
  { key: "product", label: "產品/服務風險", project: "PRODUCT_SERVICE_RISK" },
  { key: "summary", label: "最終AML/CTF風險評估", project: "FINAL_AML_CFT_RISK_ASSESSMENT" },
];

const ZUU_SIDEBAR_SECTIONS = [
  { key: "identity", label: "身份核實" },
  { key: "country", label: "國家風險" },
  { key: "industry", label: "職業資料" },
  { key: "funds", label: "資金和財富來源" },
  { key: "activity", label: "交易活動" },
  { key: "product", label: "產品風險" },
  { key: "pep", label: "識別政治人物及恐怖分子" },
  { key: "summary", label: "風險結論" },
];

type ZuuFieldConfig = {
  label: string;
  questionKey: string;
  fallbackKeys?: string[];
  kind?: ZuuRiskValueKind;
  meta?: string;
  optionLabels?: ZuuRiskOptionLabels;
};

type ZuuSectionConfig = {
  key: string;
  title: string;
  project?: string;
  fields: ZuuFieldConfig[];
};

const PAYMENT_METHOD_LABELS: ZuuRiskOptionLabels = {
  A: "支票 / 本票 / 信用卡",
  B: "入賬到銀行 / 自動櫃員機 / 保險公司",
  C: "電匯 / 銀行戶口自動轉賬",
  D: "網上銀行轉賬 / 轉數快",
  E: "第三者付款",
  F: "現金",
};

const ZUU_SECTIONS: ZuuSectionConfig[] = [
  {
    key: "identity",
    title: "身份核實",
    project: "VERIFICATION_OF_IDENTITY",
    fields: [
      {
        label: "是否已核實客戶最新的身份證明文件？",
        questionKey: "hasVerifiedLatestIdentityDocument",
        fallbackKeys: ["hasVerifiedLatestIdentityDocument_A", "validIdentityDocuments"],
        kind: "yesNo",
      },
    ],
  },
  {
    key: "country",
    title: "國家風險",
    project: "COUNTRY_RISK",
    fields: [
      {
        label: "客戶居住地的風險程度？",
        questionKey: "customerResidenceRiskLevel",
        fallbackKeys: ["customerResidenceRiskLevel_A"],
        kind: "riskLevel",
      },
    ],
  },
  {
    key: "industry",
    title: "職業資料",
    project: "OCCUPATION_DETAILS",
    fields: [
      {
        label: "客戶從事行業的風險程度？",
        questionKey: "customerIndustryRiskLevel",
        fallbackKeys: ["customerIndustryRiskLevel_A"],
        kind: "riskLevel",
      },
    ],
  },
  {
    key: "funds",
    title: "資金和財富來源",
    project: "SOURCE_OF_FOUND_AND_WEALTH",
    fields: [
      {
        label: "客戶的資金是否來自香港？",
        questionKey: "customerFundsFromHongKong",
        fallbackKeys: ["customerFundsFromHongKong_A"],
        kind: "yesNo",
      },
      {
        label: "客戶的財富來源？",
        questionKey: "customerSourceOfWealth",
        fallbackKeys: ["customerSourceOfWealth_A"],
        kind: "wealthSource",
      },
      {
        label: "客戶的財富來源補充？",
        questionKey: "customerSourceOfWealth_F_remark",
      },
      {
        label: "首期保費的繳付方式？",
        questionKey: "initialPremiumPaymentMethod",
        fallbackKeys: ["initialPremiumPaymentMethod_A"],
        optionLabels: PAYMENT_METHOD_LABELS,
      },
      {
        label: "續期保費的繳付方式？",
        questionKey: "renewalPremiumPaymentMethod",
        fallbackKeys: ["renewalPremiumPaymentMethod_A"],
        optionLabels: PAYMENT_METHOD_LABELS,
      },
    ],
  },
  {
    key: "activity",
    title: "交易活動",
    project: "TRANSACTION",
    fields: [
      {
        label: "預計客戶每年需進行的交易活動次數？",
        questionKey: "expectedAnnualTransactionCount",
        fallbackKeys: ["expectedAnnualTransactionCount_A"],
        kind: "transactionCount",
      },
    ],
  },
  {
    key: "product",
    title: "產品風險（只適用於人壽保險）",
    project: "PRODUCT_RISK",
    fields: [
      {
        label: "符合以下說明的人壽保險單？",
        questionKey: "qualifyingLifeInsurancePolicy",
        fallbackKeys: ["qualifyingLifeInsurancePolicy_A", "exceedsHighPremiumThreshold"],
        kind: "yesNo",
        meta: "年繳 / 整付保費門檻需由合規最終復核",
      },
      {
        label: "客戶與受益人是否有可保利益？",
        questionKey: "hasInsurableInterest",
        fallbackKeys: ["hasInsurableInterest_A"],
        kind: "yesNo",
      },
    ],
  },
  {
    key: "pep",
    title: "識別政治人物及恐怖分子",
    project: "IDENTIFICATION_PEP_AND_TERRORISTS",
    fields: [
      {
        label: "客戶是否與政治人物 / 恐怖分子有任何連繫？",
        questionKey: "hasPepOrTerroristConnection",
        fallbackKeys: ["hasPepOrTerroristConnection_A", "pep"],
        kind: "yesNo",
      },
    ],
  },
  {
    key: "summary",
    title: "風險結論",
    project: "FINAL_AML_CFT_RISK_ASSESSMENT",
    fields: [
      {
        label: "CRA 風險評估",
        questionKey: "craRisk",
        fallbackKeys: ["craRisk_A"],
        kind: "riskLevel",
      },
    ],
  },
];

const RiskAssessmentView: React.FC<RiskAssessmentViewProps> = ({ orderId }) => {
  const [loading, setLoading] = useState(true);
  const [craData, setCraData] = useState<OrderCra | null>(null);
  const [attachments, setAttachments] = useState<Record<string, OrderAttachmentDTO[]>>({});
  const [currentSection, setCurrentSection] = useState(
    BASE_SIDEBAR_SECTIONS[0]?.key ?? "",
  );
  const [companyList, setCompanyList] = useState<ProductCompanyListVO[]>([]);
  const [highPremiumOrders, setHighPremiumOrders] = useState<(HighPremiumOrder & { readOnly?: boolean })[]>([]);
  const [craAttachmentConfig, setCraAttachmentConfig] = useState<OrderAttachmentConfig[]>([]);
  const [craResultData, setCraResultData] = useState<unknown>(null);
  const craVersion = useMetadataStore((state) => state.craVersion);
  const craVariant = resolveCraPresentationVariant({ craVersion });
  const zuuAnswers = React.useMemo(() => parseZuuRiskAnswers(craData), [craData]);
  const zuuProjectResults = React.useMemo(
    () => parseZuuRiskProjectResults(craResultData),
    [craResultData],
  );
  const zuuProjectResultMap = React.useMemo(
    () => new Map(zuuProjectResults.map((item) => [item.project, item])),
    [zuuProjectResults],
  );
  const isZuuRiskView = craVariant === "zuu";

  // 动态生成侧边栏菜单项
  const sidebarSections = React.useMemo(() => {
    const sections = [...(isZuuRiskView ? ZUU_SIDEBAR_SECTIONS : BASE_SIDEBAR_SECTIONS)];
    // 只有在配置不为空时才显示附件菜单项
    if (craAttachmentConfig.length > 0) {
      sections.push({ key: "attachments", label: "額外財務審核資料及檔案" });
    }
    return sections;
  }, [craAttachmentConfig, isZuuRiskView]);

  const genericProjectMap = React.useMemo(
    () =>
      new Map(
        BASE_SIDEBAR_SECTIONS.filter(
          (section): section is (typeof BASE_SIDEBAR_SECTIONS)[number] & { project: string } =>
            Boolean(section.project),
        ).map((section) => [section.key, section.project]),
      ),
    [],
  );

  useEffect(() => {
    if (sidebarSections.length === 0) return;

    const hasCurrentSection = sidebarSections.some(
      (section) => section.key === currentSection,
    );

    if (!hasCurrentSection) {
      setCurrentSection(sidebarSections[0].key);
    }
  }, [currentSection, sidebarSections]);

  // Preview Modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
  }>();

  useEffect(() => {
    if (!orderId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // 获取公司列表
        const companies = await getProductCompanyList();
        setCompanyList(companies || []);

        // 获取订单详情
        await getOrderDetail(Number(orderId));

        // 获取 CRA 数据
        const [cra, craResult] = await Promise.all([
          getRiskEvaluation(Number(orderId)),
          getCraResult(Number(orderId)).catch((error) => {
            console.error("Failed to fetch CRA result data:", error);
            return null;
          }),
        ]);
        setCraResultData(craResult);

        // 解析 answer 數組,構建便於訪問的數據結構
        if (cra?.answer && Array.isArray(cra.answer)) {
          const parsedData: any = { ...cra };

          cra.answer.forEach((item: any) => {
            const key = item.question;
            if (!key) return;

            if (item.type === "RADIO") {
              // 單選題:找到 value: true 的項目
              const selectedItem = item.items?.find((opt: any) => opt.value === true);
              if (selectedItem) {
                parsedData[key] = selectedItem.key;
              }
            } else if (item.type === "CHECKBOX") {
              // 多選題:收集所有 value: true 的項目
              const selectedItems = item.items?.filter((opt: any) => opt.value === true);
              if (selectedItems && selectedItems.length > 0) {
                parsedData[key] = selectedItems.map((opt: any) => opt.key);
              }
            } else if (item.type === "INPUT") {
              // 輸入題:使用 value 或第一個 item 的 value
              if (item.value !== undefined) {
                parsedData[key] = item.value;
              } else if (item.items && item.items.length > 0) {
                parsedData[key] = item.items[0].value;
              }
            }
          });

          setCraData(parsedData);
          console.log("parsedData", parsedData);
          // 设置外部保单数据
          setHighPremiumOrders(cra.insuredPolicy.highPremiumOrders || []);
        } else {
          setCraData(cra);
          setHighPremiumOrders(cra?.insuredPolicy?.highPremiumOrders || []);
        }

        // 获取附件
        const craTypes = [
          FileType.CraBrokerReport,
          FileType.CraFinancialQuestionnaire,
          FileType.RiskEvaluationFormAttachment,
          FileType.CraAssetIncomeProof,
          FileType.CraThiredPartyRelaTionShipCertificate,
        ];

        const attachmentMap: Record<string, OrderAttachmentDTO[]> = {};
        for (const type of craTypes) {
          const result = await getOrderAttachments(Number(orderId), type);
          if (result && result.length > 0) {
            attachmentMap[type] = result;
          }
        }
        setAttachments(attachmentMap);

        // 获取 CRA 附件配置
        const config = await getCraAttachmentConfig(Number(orderId));
        if (config && Array.isArray(config)) {
          setCraAttachmentConfig(config);
        }
      } catch (error) {
        console.error("Failed to fetch CRA data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId]);

  // Custom InfoCard component
  const InfoCard = ({ title, children }: { title: string; children?: React.ReactNode }) => (
    <div className={styles["info-card"]}>
      <div className={styles["card-header"]}>
        <h3 className={styles["card-title"]}>{title}</h3>
      </div>
      {children && <div className={styles["card-body"]}>{children}</div>}
    </div>
  );

  // 辅助函数：渲染答案
  const renderAnswer = (value: string | undefined) => {
    if (!value) return <Tag color="default">未填写</Tag>;

    const answerMap: Record<string, { text: string; color: string }> = {
      A: { text: "A: 是", color: "green" },
      B: { text: "B: 否", color: "red" },
      C: { text: "C: N/A", color: "blue" },
    };

    const answer = answerMap[value] || { text: value, color: "default" };
    return (
      <Tag color={answer.color} style={{ fontSize: 14, padding: "2px 8px", borderRadius: 4 }}>
        {answer.text}
      </Tag>
    );
  };

  // 辅助函数：渲染多选答案
  const renderMultipleAnswer = (value: string | string[] | undefined) => {
    if (!value) return <Tag color="default">未填写</Tag>;
    const values = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];

    // 支付方式映射
    const paymentMethodMap: Record<string, string> = {
      A: "A: 信用卡",
      B: "B: 銀行本票",
      C: "C: 電匯",
      D: "D: 銀行轉賬",
      E: "E: 現金(透過保險公司或銀行櫃檯支付)",
      F: "F: 自動扣款",
      G: "G: 支票",
    };

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {values.map((v, idx) => (
          <Tag key={idx} color="blue">
            {paymentMethodMap[v] || v}
          </Tag>
        ))}
      </div>
    );
  };

  const getZuuToneColor = (kind: ZuuRiskValueKind | undefined, displayValue: string) => {
    if (displayValue === "--") return "default";

    if (kind === "yesNo") {
      return displayValue === "是" ? "green" : "red";
    }

    if (kind === "riskLevel") {
      if (displayValue.includes("低")) return "green";
      if (displayValue.includes("中")) return "orange";
      if (displayValue.includes("高")) return "red";
    }

    return "blue";
  };

  // 辅助函数：渲染附件
  const renderAttachments = (fileType: FileType) => {
    const files = attachments[fileType];
    if (!files || files.length === 0) {
      return <div style={{ color: "#999" }}>暫無檔案</div>;
    }

    const handlePreview = (file: OrderAttachmentDTO) => {
      const filePath = file.ossPath || file.path || file.url;
      if (!filePath) {
        return;
      }
      setPreviewFile({ url: filePath, name: file.name || "unnamed" });
      setPreviewOpen(true);
    };

    const getFileIcon = (fileName: string) => {
      const name = fileName.toLowerCase();
      if (name.endsWith(".pdf")) return pdfIcon;
      if (/\.(xls|xlsx|csv)$/.test(name)) return excelIcon;
      if (/\.(doc|docx)$/.test(name)) return wordIcon;
      return null;
    };

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {files.map((file, idx) => {
          const fileName = file.name || "unnamed";
          const icon = getFileIcon(fileName);
          const isImage = !icon && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName);

          return (
            <div
              key={idx}
              onClick={() => handlePreview(file)}
              style={{
                width: 160,
                height: 110,
                padding: 8,
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#1890ff";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(24, 144, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#d9d9d9";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {icon ? (
                <img src={icon} alt={fileName} style={{ width: 60, height: 60, objectFit: "contain" }} />
              ) : isImage ? (
                <img src={file.url || file.path} alt={fileName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={{ fontSize: 40, color: "#999" }}>📄</div>
              )}
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: "#666",
                  textAlign: "center",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  padding: "0 4px",
                }}
                title={fileName}
              >
                {fileName}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 渲染不同 section 的内容
  const renderSectionContent = () => {
    if (!craData) return null;

    const renderProjectResult = (project?: string) => {
      if (!project) return null;

      const projectResult = zuuProjectResultMap.get(project);
      const displayValue = formatZuuRiskValue(projectResult?.result ?? "--", "riskLevel");

      return (
        <Descriptions
          column={1}
          labelStyle={{ fontWeight: 500, width: "60%" }}
          contentStyle={{ textAlign: "right" }}
        >
          <Descriptions.Item label="評估結果">
            <Tag color={getZuuToneColor("riskLevel", displayValue)}>{displayValue}</Tag>
            {projectResult?.reason?.trim() ? (
              <div style={{ marginTop: 4, color: "#666", fontSize: 12 }}>
                原因：{projectResult.reason}
              </div>
            ) : null}
          </Descriptions.Item>
        </Descriptions>
      );
    };

    if (isZuuRiskView) {
      const section = ZUU_SECTIONS.find((item) => item.key === currentSection);
      const qualifyingLifePolicy = resolveZuuRiskAnswer(zuuAnswers, ["qualifyingLifeInsurancePolicy", "qualifyingLifeInsurancePolicy_A", "exceedsHighPremiumThreshold"]);

      if (!section) {
        if (currentSection === "attachments") {
          return (
            <InfoCard title="額外財務審核資料及檔案">
              <Row gutter={[16, 16]}>
                {craAttachmentConfig.map((config) => {
                  if (!config.type) return null;
                  return (
                    <Col key={config.type} span={12}>
                      <div style={{ marginBottom: 8, fontWeight: 500 }}>
                        {config.required && <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>}
                        {config.name || config.type}
                      </div>
                      {renderAttachments(config.type as FileType)}
                    </Col>
                  );
                })}
              </Row>
            </InfoCard>
          );
        }

        return null;
      }

      return (
        <InfoCard title={section.title}>
          {renderProjectResult(section.project)}
          <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
            {section.fields.map((field) => {
              const answer = resolveZuuRiskAnswer(zuuAnswers, [field.questionKey, ...(field.fallbackKeys ?? [])]);
              const displayValue = answer ? formatZuuRiskValue(answer.value, field.kind ?? "plain", field.optionLabels) : "--";
              const color = getZuuToneColor(field.kind, displayValue);

              return (
                <Descriptions.Item key={field.label} label={field.label}>
                  {field.kind === "yesNo" || field.kind === "riskLevel" || displayValue === "--" ? <Tag color={color}>{displayValue}</Tag> : <span>{displayValue}</span>}
                  {/* <div style={{ marginTop: 4, color: "#666", fontSize: 12 }}>
                    {field.meta ?? (answer ? `來源字段：${answer.key}` : "暫無問卷答案")}
                  </div> */}
                </Descriptions.Item>
              );
            })}
          </Descriptions>

          {section.key === "product" && qualifyingLifePolicy?.value === "A" && (
            <div style={{ marginTop: 24 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, color: "#333", fontWeight: 500 }}>若上方問題之答案為「是」者，請在下方查看客戶已投保的保單。</div>
                <div style={{ fontWeight: "bold", fontSize: 16 }}>
                  年繳總交保費： {craData?.insuredPolicy?.annualTotalPremium ? `${Number(craData.insuredPolicy.annualTotalPremium).toLocaleString()}` : "0"}HK$ &nbsp;&nbsp; 一次性繳付保費（含預繳）：{" "}
                  {craData?.insuredPolicy?.totalOneTimePayment ? `${Number(craData.insuredPolicy.totalOneTimePayment).toLocaleString()}` : "0"}HK$
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                {highPremiumOrders.length === 0 ? (
                  <div
                    style={{
                      padding: 40,
                      textAlign: "center",
                      background: "#fafafa",
                      borderRadius: 8,
                      color: "#999",
                    }}
                  >
                    暫無外部保單
                  </div>
                ) : (
                  highPremiumOrders.map((order: any, index: number) => (
                    <HighPremiumOrderCard
                      key={order.key || order.id || `temp-${index}`}
                      order={{
                        ...order,
                        key: order.key || order.id || `temp-${index}`,
                      }}
                      companyList={companyList}
                      onEdit={() => {}}
                      onDelete={() => {}}
                      readOnly={true}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </InfoCard>
      );
    }

    switch (currentSection) {
      case "customerRisk":
        return (
          <>
            <InfoCard title="客戶風險（客戶背景）">
              {renderProjectResult(genericProjectMap.get(currentSection))}
              <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
                <Descriptions.Item label="客戶是否是政治公眾人物（PEP）嗎？">{renderAnswer(craData.pep)}</Descriptions.Item>
                <Descriptions.Item label="（僅限公司客戶）是否擁有所有實益擁有人和董事/控制人的完整名單">{renderAnswer(craData.fullUboAndDirectorList)}</Descriptions.Item>
                <Descriptions.Item label="您的客戶有如下情況嗎？（高淨值人士、石油天然氣等）">{renderAnswer(craData.customerMediumRiskSituation)}</Descriptions.Item>
                <Descriptions.Item label="您的客戶有如下情況嗎？（賭場賭博、貴金屬、軍火商等）">{renderAnswer(craData.customerHighRiskSituation)}</Descriptions.Item>
              </Descriptions>
            </InfoCard>

            <InfoCard title="客戶風險（身份和地址驗證）">
              <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
                <Descriptions.Item label="您的客戶是否提供了可接受的標準身份驗證檔案？">{renderAnswer(craData.validIdentityDocuments)}</Descriptions.Item>
                <Descriptions.Item label="您的客戶在過程中是否配合？">{renderAnswer(craData.cooperative)}</Descriptions.Item>
              </Descriptions>
            </InfoCard>

            <InfoCard title="客戶風險（客戶的財務狀況）">
              <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
                <Descriptions.Item label="客戶陳述的財富來源/資金來源以及涉及的金額是否與您對客戶的了解相符？">{renderAnswer(craData.sourceOfFundsConsistent)}</Descriptions.Item>
                <Descriptions.Item label="您的客戶不涉及參與/經營高風險或高現金週轉業務？">{renderAnswer(craData.notHighRiskBusiness)}</Descriptions.Item>
              </Descriptions>
            </InfoCard>
          </>
        );

      case "payment":
        return (
          <InfoCard title="保費支付方式">
            <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
              <Descriptions.Item label="投保申請時是否繳付了首期保費">{renderAnswer(craData.paidInitialPremium)}</Descriptions.Item>
              <Descriptions.Item label="保費/投資金額是否來自客戶自己的賬戶">{renderAnswer(craData.fromOwnAccount)}</Descriptions.Item>
              {craData.fromOwnAccount === "B" && (
                <>
                  <Descriptions.Item label="與第三方付款人關係">
                    <Tag color="blue">{craData.thirdPartyPayerRelation || "未填写"}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="備註">{craData.thirdPartyPayerInfo || "-"}</Descriptions.Item>
                  <Descriptions.Item label="關係證明文件">{renderAttachments(FileType.CraThiredPartyRelaTionShipCertificate)}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="客戶計劃使用哪種方法來支付初始保費？">{renderMultipleAnswer(craData.initialPremiumPaymentMethod)}</Descriptions.Item>
            </Descriptions>
          </InfoCard>
        );

      case "distribution":
        return (
          <>
            <InfoCard title="分銷/交付風險（分銷管道）">
              {renderProjectResult(genericProjectMap.get(currentSection))}
              <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
                <Descriptions.Item label="客戶是否由第三方介紹">{renderAnswer(craData.referredByThirdParty)}</Descriptions.Item>
                {craData.referredByThirdParty !== "B" && (
                  <Descriptions.Item label="客戶的投保指示是否通過第三方傳達？">
                    {renderAnswer(craData.instructionThroughThirdParty)}
                    {craData.instructionThroughThirdParty === "A" && craData.thirdPartyInstructionProof && (
                      <div style={{ marginTop: 4, color: "#666" }}>原因: {craData.thirdPartyInstructionProof}</div>
                    )}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </InfoCard>

            <InfoCard title="分銷/交付風險（面對面溝通）">
              <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
                <Descriptions.Item label="在投保過程中，您是否經面對面方式與客戶進行溝通？">{renderAnswer(craData.faceToFaceCommunication)}</Descriptions.Item>
                <Descriptions.Item label="在與客戶（非面對面）溝通的情況下，您是否認為需要有適當的保障措施？">{renderAnswer(craData.needsNonFaceToFaceSafeguards)}</Descriptions.Item>
              </Descriptions>
            </InfoCard>
          </>
        );

      case "location":
        return (
          <InfoCard title="國家或地區風險">
            {renderProjectResult(genericProjectMap.get(currentSection))}
            <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
              <Descriptions.Item label="您的客戶定居在香港嗎？">{renderAnswer(craData.residentInHK)}</Descriptions.Item>
              {craData.residentInHK === "B" && <Descriptions.Item label="如果您的客戶不在香港定居，他/她是否位於下列國家之一？">{renderAnswer(craData.nonHKResidenceCategory)}</Descriptions.Item>}
            </Descriptions>
          </InfoCard>
        );

      case "product": {
        // Assign keys to highPremiumOrders if missing
        const productData = highPremiumOrders.map((item: any, index: number) => ({
          ...item,
          key: item.key || item.id || `temp-${index}`,
        }));

        return (
          <InfoCard title="產品/服務風險">
            {renderProjectResult(genericProjectMap.get(currentSection))}
            <Descriptions column={1} labelStyle={{ fontWeight: 500, width: "60%" }} contentStyle={{ textAlign: "right" }}>
              <Descriptions.Item label="產品/服務本質上是否提供了更多的匿名性？">{renderAnswer(craData.providesAnonymity)}</Descriptions.Item>
              <Descriptions.Item label="提供給客戶的產品/服務是否涉及收取大量現金？">{renderAnswer(craData.involvesLargeCash)}</Descriptions.Item>
              <Descriptions.Item label="客戶在過去三個月內多次購買相同類型的保險產品，且沒有有效理由">{renderAnswer(craData.frequentPurchasesWithoutReason)}</Descriptions.Item>
              <Descriptions.Item label="這些產品/服務是否有利於高交易頻率和不規則的客戶賬戶活動？">{renderAnswer(craData.supportsHighFrequencyIrregularActivity)}</Descriptions.Item>
              <Descriptions.Item label="產品是否允許大額提款？">{renderAnswer(craData.allowsLargeWithdrawals)}</Descriptions.Item>
              <Descriptions.Item label="在職人士:年繳交保費≥HKD2,000,000 或一次性繳付保費（包含預繳）≥ HKD4,000,000。非在職人士（家庭主婦/學生/退休人士/無業）或年齡在65歲以上的申請:年繳交保費≥HKD1,000,000 或一次性繳付保費（包含預繳）≥ HKD2,000,000。(累積客戶在過去六個月內購買保險產品的保費)">
                {renderAnswer(craData.exceedsHighPremiumThreshold)}
              </Descriptions.Item>
            </Descriptions>

            {/* 外部保单展示 - 当 exceedsHighPremiumThreshold 为 A 时 */}
            {craData.exceedsHighPremiumThreshold === "A" && (
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, color: "#333", fontWeight: 500 }}>若上方問題之答案為「是」者，請在下方查看客戶已投保的保單。</div>
                  <div style={{ fontWeight: "bold", fontSize: 16 }}>
                    年繳總交保費： {craData?.insuredPolicy?.annualTotalPremium ? `${Number(craData.insuredPolicy.annualTotalPremium).toLocaleString()}` : "0"}HK$ &nbsp;&nbsp;
                    一次性繳付保費（含預繳）： {craData?.insuredPolicy?.totalOneTimePayment ? `${Number(craData.insuredPolicy.totalOneTimePayment).toLocaleString()}` : "0"}HK$
                  </div>
                </div>

                {/* 外部保单卡片列表 */}
                <div style={{ marginBottom: 16 }}>
                  {productData.length === 0 ? (
                    <div
                      style={{
                        padding: 40,
                        textAlign: "center",
                        background: "#fafafa",
                        borderRadius: 8,
                        color: "#999",
                      }}
                    >
                      暫無外部保單
                    </div>
                  ) : (
                    productData.map((order) => <HighPremiumOrderCard key={order.key} order={order} companyList={companyList} onEdit={() => {}} onDelete={() => {}} readOnly={true} />)
                  )}
                </div>
              </div>
            )}
          </InfoCard>
        );
      }

      case "summary":
        return (
          <InfoCard title="最終AML/CTF風險評估">
            {renderProjectResult(genericProjectMap.get(currentSection))}
          </InfoCard>
        );

      case "attachments":
        return (
          <InfoCard title="額外財務審核資料及檔案">
            <Row gutter={[16, 16]}>
              {craAttachmentConfig.map((config) => {
                if (!config.type) return null;
                return (
                  <Col key={config.type} span={12}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>
                      {config.required && <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>}
                      {config.name || config.type}
                    </div>
                    {renderAttachments(config.type as FileType)}
                  </Col>
                );
              })}
            </Row>
          </InfoCard>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={styles["loading-container"]}>
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  if (!craData) {
    return (
      <div className={styles["empty-container"]}>
        <Empty description="暫無風險評估數據" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles["main-layout"]}>
        {/* Sidebar */}
        <div className={styles.sidebar}>
          {sidebarSections.map((section) => (
            <div key={section.key} className={`${styles["sidebar-item"]} ${currentSection === section.key ? styles.active : ""}`} onClick={() => setCurrentSection(section.key)}>
              <div className={styles["sidebar-item-content"]}>{section.label}</div>
            </div>
          ))}
        </div>

        {/* Content Area */}
        <div className={styles["form-area"]}>
          <div className={styles["detail-content"]}>{renderSectionContent()}</div>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} fileUrl={previewFile?.url} fileName={previewFile?.name} />
    </div>
  );
};

export default RiskAssessmentView;
